import {Injectable} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ServiceLoopbackFilters} from '../base-service/base.service';
import {timeZonesData} from '../../config/data/time-zones/time-zones.data';
import {MessageAlertService} from '../message-alert/message-alert.service';
import {differenceInCalendarDays} from 'date-fns';
import {GenderEnum} from '../../models/user';
import analyze from 'rgbaster';
import {NgForm} from '@angular/forms';
import {MeasurementRequestDTO} from '../../dtos/measurement';
import {IMeasurementRow, MeasurementRowEnum} from '../../components/new-measurement/new-measurement.component';
import {IMealPlan, IMealPlanResponse} from '../../models/meal-plans';
import {NzSelectComponent} from 'ng-zorro-antd/select';
import {firstValueFrom, Observable, Observer} from 'rxjs';
import {StorageService} from '../storage/storage.service';
import {Store} from '@ngxs/store';
import {DeviceTypeEnum} from '../../enums/utils';
import {omit} from 'lodash';
import {BIOCHEMICAL_DATA} from '../../config/data/patient-record';
import {PanelUtilitiesActions} from '../../models/panel-utilities';
import {AppTourService} from '../app-tour/app-tour.service';

@Injectable({
  providedIn: 'root'
})
export class UtilitiesService {

  constructor(
    public alertService: MessageAlertService,
    public storageService: StorageService,
    public store: Store,
  ) { }

  /**
   * Get all params
   * @param activatedRoute ActivatedRoute object
   */
  getAllParams(activatedRoute: ActivatedRoute): any {
    return activatedRoute.snapshot.params;
  }

  /**
   * Get all params
   * @param activatedRoute ActivatedRoute object
   */
  getAllQueryParams(activatedRoute: ActivatedRoute): any {
    return activatedRoute.snapshot.queryParams;
  }

  /**
   * método que configura un filtro para obtener elementos paginados cuando se usan parameters skip y limit
   * @param indexPage indice actual del paginador
   * @param nzPageSize número de elementos por página
   */
  filterCountPagination(indexPage: number, nzPageSize: number): { params: { skip: number, limit: number, status?: string } } {
    return {
      params: {
        skip: indexPage === 1 ? 0 : (indexPage - 1) * nzPageSize,
        limit: nzPageSize
      }
    };
  }

  /**
   * método que configura un filtro para obtener elementos paginados usando los filtros de loopback
   * @param indexPage índice actual del paginador
   * @param nzPageSize número de elementos por página
   */
  filterCountPaginationLoopback(indexPage: number, nzPageSize: number): ServiceLoopbackFilters {
    return {
      skip: indexPage === 1 ? 0 : (indexPage - 1) * nzPageSize,
      limit: nzPageSize
    };
  }

  /**
   * método para mostrar u ocultar la contraseña escrita en un input
   * @param event valor del input
   * @param passwordInput elemento input
   */
  switchPassword(event, passwordInput): void {
    event.stopPropagation();
    event.preventDefault();
    passwordInput.type = passwordInput.type === 'password' ? 'text' : 'password';
  }

  /**
   * Obtiene la zona horaria del navegador
   */
  getTimeZoneBrowser(): string {
    const timezoneUTC = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timeZonesData.getTextNameByUTC(timezoneUTC);
  }

  /**
   * Valida que un archivo sea de tipo imagen
   * @param file File archivo a validar la extension
   * @param extensions string[] extensiones a validar
   * @param showExtensionValidationError boolean mostrar o no el mensaje de error
   */
  validateExtensionImage(file: File, extensions = ['jpg', 'jpeg', 'png', 'gif'], showExtensionValidationError = true): boolean {
    const extension = file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length) || file.name;
    const isValid = (extensions.length > 0 && extensions.map(ext => ext.toLowerCase()).includes(extension.toLowerCase()));
    if (!isValid && showExtensionValidationError) {
      this.alertService.createMessage('error', 'La extensión del archivo es errónea, Aceptadas: ' + extensions.join(', '), 5000);
    }
    return isValid;
  }

  /**
   * Para evitar que se pueda seleccionar una fecha futura
   * @param current fecha
   */
  disabledDate = (current: Date): boolean => {
    return differenceInCalendarDays(current, new Date()) > 0 || differenceInCalendarDays(current, new Date(1900, 0, 1)) < 0;
  }

  /**
   * Para evitar que se pueda seleccionar una fecha pasada
   * @param current fecha
   */
  disabledPastDate = (current: Date): boolean =>
    differenceInCalendarDays(current, new Date()) < 0

  /**
   * agrega una imagen temporal al usuario dependiendo de su genero y tipo, siempre y cuando la imagen que tenga
   * no se pueda visualizar
   * @param gender genero del usuario
   * @param user usuario
   * @param userType tipo de usuario EXPERT | PATIENT
   */
  loadDefaultImage(gender: GenderEnum | string, user, userType: string): void {
    // tslint:disable-next-line:no-string-literal
    user.profileImageURL = (userType === 'patient' ?
      (
        gender ?
          (gender === GenderEnum.MALE ? './assets/images/icons/icons8-user-100.png' :
            gender === GenderEnum.FEMALE ? './assets/images/icons/icons8-lady-100.png' :
              './assets/images/icons/default-user.png')
          : './assets/images/icons/default-user.png'
      ) :
      userType === 'expert' ?
        (
          gender ?
            (gender === GenderEnum.MALE ? './assets/images/icons/icons8-doctor-male-100.png' :
              gender === GenderEnum.FEMALE ? './assets/images/icons/icons8-doctor-female-100.png' :
                './assets/images/icons/default-user.png')
            : './assets/images/icons/default-user.png'
        ) :
        './assets/images/icons/default-user.png');
  }

  async getPredominantImageColor(img: string): Promise<{ color: string, count: number }[]> {
    return await analyze(img);
  }

  formIsValid(form: NgForm): boolean {
    const controls = form.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        return false;
      }
    }
    return true;
  }

  /**
   * concatena los valores de arreglos anidados
   * @param arr1 arreglo
   */
  flattenDeep(arr1): any[] {
    return arr1.reduce((acc, val) => Array.isArray(val) ? acc.concat(this.flattenDeep(val)) : acc.concat(val), []);
  }

  convertMeasurementsRowToMeasurements(measurementsRow: IMeasurementRow[], rowEnum = MeasurementRowEnum): MeasurementRequestDTO[] {
    const measurements: MeasurementRequestDTO[] = [];
    measurementsRow.forEach(item => {
      if (item.status !== rowEnum.NEW) {
        measurements.push(
          {
            value: item.value,
            measureUnitId: item.unit,
            measureId: item._id
          }
        );
      }
    });
    return measurements;
  }

  convertMealPlanToDTO(mealPlan: IMealPlan): IMealPlanResponse {
    return {
      name: mealPlan.name,
      planPeriods: mealPlan.planPeriods,
      _id: mealPlan._id,
      nutritionalProperties: mealPlan.nutritionalProperties,
      instructions: mealPlan.instructions,
      hiddenOnList: mealPlan.hiddenOnList,
      expertId: mealPlan.expertId,
      expert: null
    };
  }

  secondsToHoursMinutesSeconds(secondsTotal: number): { hours: number, minutes: number, seconds: number } {
    const hours = Math.floor(secondsTotal / 3600);
    const minutes = Math.floor((secondsTotal / 60) % 60);
    const seconds = secondsTotal % 60;
    return { hours, minutes, seconds };
  }

  async truncateFilterSelectOptions(filterSelect: NzSelectComponent): Promise<number | undefined> {
    const selectWidth: number | undefined = filterSelect?.originElement?.nativeElement?.offsetWidth;
    if (!filterSelect) {
      return new Promise((resolve) => {
        resolve(undefined);
      });
    }
    const children: any[] = filterSelect?.originElement?.nativeElement?.children;
    let optionsWidth = 0;
    let maxTagFilterCount = 0;
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < children.length; i++) {
      const item = children[i];
      let itemWidth = item.offsetWidth;
      switch (item.nodeName) {
        case 'NZ-SELECT-ITEM':
          itemWidth += 5;
          break;
        case 'NZ-SELECT-SEARCH':
          itemWidth += 8;
          break;
      }
      const permit = await this.permitOtherOption(selectWidth, optionsWidth + itemWidth + 150);
      if (permit) {
        optionsWidth += itemWidth + 10;
        maxTagFilterCount++;
      } else {
        break;
      }
    }
    if (maxTagFilterCount < 0) maxTagFilterCount = 0;
    return new Promise(resolve => {
      resolve(maxTagFilterCount);
    });
  }

  permitOtherOption(selectWidth: number, itemWidth: number): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (selectWidth > itemWidth) {
          resolve(true);
        } else {
          resolve(false);
        }
      }, 1);
    });
  }

  /**
   * this method find dom element for a selector
   */
  existElementInDom(): {
    byId: (id: string, options?: {maxAttempts?: number, timeout?: number}) => Observable<HTMLElement | null>,
    byClassName: (className: string, item?: number, options?: {maxAttempts?: number, timeout?: number}) => Observable<Element | null>,
  } {
    return {
      byId: (id: string, options: {maxAttempts?: number, timeout?: number} = {maxAttempts: 10, timeout: 500}) => {
        let interval = null;
        let count = 0;
        return new Observable((observer: Observer<HTMLElement | null>) => {
          interval = setInterval(() => {
            const element = document.getElementById(id);
            if (count >= options.maxAttempts) {
              clearInterval(interval);
              observer.next(null);
              observer.complete();
              // console.log('ELEMENT NOT FOUND');
            }

            if (element) {
              clearInterval(interval);
              observer.next(element);
              observer.complete();
              // console.log('CLEAR INTERVAL');
            } else {
              count++;
              observer.next(null);
              // console.log('TRY: ', count);
            }
          }, options.timeout);
        });
      },
      byClassName: (className: string, indexItem = 0, options: {maxAttempts?: number, timeout?: number} = {maxAttempts: 10, timeout: 500}) => {

        let interval = null;
        let count = 0;
        return new Observable((observer: Observer<Element | null>) => {
          interval = setInterval(() => {
            const element = document.getElementsByClassName(className).item(indexItem);
            if (count >= options.maxAttempts) {
              clearInterval(interval);
              observer.next(null);
              observer.complete();
              // console.log('ELEMENT NOT FOUND');
            }

            if (element) {
              clearInterval(interval);
              observer.next(element);
              observer.complete();
              // console.log('CLEAR INTERVAL');
            } else {
              count++;
              observer.next(null);
              // console.log('TRY: ', count);
            }
          }, options.timeout);
        });
      },
      // ... add another find element
    };

  }

  /**
   * Show or hide typebot
   * @param show if show is true it shows typebot otherwise it hides it
   */
  showOrHideTypebot(show = true): void {
    // Hide button
    const typebotBubbleButton = (document.querySelector('#typebot-bubble-button') as HTMLElement);
    if (typebotBubbleButton) typebotBubbleButton.style.display = show ? 'flex' : 'none';
    // Hide proactive-message
    const typebotBubbleProactiveMessage = (document.querySelector('#typebot-bubble .proactive-message') as HTMLElement);
    if (typebotBubbleProactiveMessage) typebotBubbleProactiveMessage.style.display = show ? 'flex' : 'none';
  }

  /**
   * Determine if the device is mobile, tablet or desktop
   */
  getDeviceType(): DeviceTypeEnum {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return DeviceTypeEnum.TABLET;
    }
    if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return DeviceTypeEnum.MOBILE;
    }
    return DeviceTypeEnum.DESKTOP;
  }

  /**
   * Scroll to html element
   * @param elementSelector string selector
   * @param scrollIntoViewOptions options
   */
  scrollToElement(elementSelector: string, scrollIntoViewOptions: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center', inline: 'center'}): void {
    const element: Element = document.querySelector(elementSelector);
    element.scrollIntoView(scrollIntoViewOptions);
  }

  removeEmptyProperties(obj: any): any {
    if (typeof obj === 'object' && !Array.isArray(obj) && !(obj instanceof Date)) {
        return Object.entries(obj)
                .filter(([k, v]) => ![null].includes(v as string))
                .reduce((r, [key, value]) => ({ ...r, [key]: this.removeEmptyProperties(value) }), {});
      } else {
      return obj;
    }
  }

  /**
   * Return Array of biochemical, use in mine sphere
   */
  getBiochemicalArray(item: any): any {
    if (item === void 0 ) return [];

    return Object.entries(
      omit(item, ['score', 'totalPercentage', '...others'])
    ).map(([name, value]: [string, number]) => {
      const biochemical = BIOCHEMICAL_DATA.find((bio) => bio.name === name);
      if (!biochemical) return null;
      return {
        name: biochemical.name,
        nameEs: biochemical.nameEs,
        value,
        measureUnitSymbolEs: '',
        range: {
          min: 0,
          max: 0,
        }
      };
      // tslint:disable-next-line:no-shadowed-variable
    }).filter((item) => {
      // tslint:disable-next-line:no-unused-expression
      item && item.value !== 0;
    });
  }

  /**
   * Usado para evitar que marque error el linter dentro de un bloque try
   * @param message
   */
  throwError(message: string): void {
    throw new Error(message);
  }

  /**
   * cierra el panel de utilidades
   */
  async closePanelUtilities(): Promise<void> {
    if (window.innerWidth <= 768) {
      setTimeout(async () => {
        await firstValueFrom(this.store.dispatch(new PanelUtilitiesActions.SetIsVisible(false)));
      }, 500);
    }
  }
}
